// App-wide access to the authenticated user's preferences. Mounted in the admin shell so it loads once
// after login: it hydrates the dashboard clock store (localStorage + event) and holds appearance +
// notification config for the settings tabs. Saves write to the backend and update local state.

import React, { createContext, useCallback, useContext, useEffect, useState } from "react";
import {
  fetchAllPreferences,
  saveAppearancePreference,
  saveClockPreference,
  saveNotificationPreference,
} from "utils/preferencesService";
import { saveClockSettings, DEFAULT_CLOCKS } from "utils/clockSettings";

const PreferencesContext = createContext(null);

const NOOP = async () => {};
const FALLBACK = {
  appearance: null,
  notifications: null,
  loaded: false,
  saveAppearance: NOOP,
  saveNotifications: NOOP,
  saveClocks: NOOP,
};

export const PreferencesProvider = ({ children }) => {
  const [appearance, setAppearance] = useState(null);
  const [notifications, setNotifications] = useState(null);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    let active = true;
    if (!localStorage.getItem("token")) {
      setLoaded(true);
      return undefined;
    }
    fetchAllPreferences()
      .then((all) => {
        if (!active) return;
        if (all?.appearance) setAppearance(all.appearance);
        if (all?.notifications) setNotifications(all.notifications);
        // Hydrate per-user clock cache from the server (empty list → app defaults for this user).
        if (Array.isArray(all?.clocks)) {
          const clocks = all.clocks.length > 0 ? all.clocks : DEFAULT_CLOCKS.map((c) => ({ ...c }));
          saveClockSettings(clocks);
        }
      })
      .catch(() => {
        /* offline / unauthorized — components fall back to their local defaults */
      })
      .finally(() => {
        if (active) setLoaded(true);
      });
    return () => {
      active = false;
    };
  }, []);

  const saveAppearance = useCallback(async (value) => {
    setAppearance(value);
    await saveAppearancePreference(value);
  }, []);

  const saveNotifications = useCallback(async (value) => {
    setNotifications(value);
    await saveNotificationPreference(value);
  }, []);

  const saveClocks = useCallback(async (clocks) => {
    await saveClockPreference(clocks);
  }, []);

  return (
    <PreferencesContext.Provider
      value={{ appearance, notifications, loaded, saveAppearance, saveNotifications, saveClocks }}
    >
      {children}
    </PreferencesContext.Provider>
  );
};

export const usePreferences = () => useContext(PreferencesContext) ?? FALLBACK;

export default PreferencesContext;
